import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { SignatureModal, FeedbackModal } from '@/components/admin/SignatureCanvas'
import { usePublicDevis } from '@/hooks/useDevis'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { SignatureData } from '@/types/devis'
import {
  FileText,
  Check,
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Building2,
  Mail,
} from 'lucide-react'

function NotFound({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-beige-50 flex items-center justify-center p-4">
      <Card padding="lg" className="max-w-md text-center">
        <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-gold-500" />
        <h1 className="text-xl font-bold text-brown-900 mb-2">Devis introuvable</h1>
        <p className="text-brown-600">{message}</p>
      </Card>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-beige-50 flex items-center justify-center">
      <Spinner />
    </div>
  )
}

export default function QuoteViewPage() {
  const { token } = useParams<{ token: string }>()
  const {
    devis,
    isLoading,
    error,
    signDevis,
    rejectDevis,
    isSigning,
    isRejecting,
  } = usePublicDevis(token || '')

  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [signatureSuccess, setSignatureSuccess] = useState(false)
  const [feedbackSuccess, setFeedbackSuccess] = useState(false)

  if (isLoading) return <LoadingState />
  if (error || !devis) return <NotFound message="Ce devis n'existe pas ou n'est plus disponible." />

  const isExpired = new Date(devis.date_validite) < new Date()
  const canInteract = !isExpired && !['accepted', 'rejected', 'converted'].includes(devis.status)

  const handleSign = async (signatureData: SignatureData) => {
    try {
      await signDevis(signatureData)
      setShowSignatureModal(false)
      setSignatureSuccess(true)
    } catch (err) {
      console.error('Erreur lors de la signature:', err)
    }
  }

  const handleReject = async (message: string) => {
    try {
      await rejectDevis(message)
      setShowFeedbackModal(false)
      setFeedbackSuccess(true)
    } catch (err) {
      console.error('Erreur lors du refus:', err)
    }
  }

  return (
    <div className="min-h-screen bg-beige-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success messages */}
        {signatureSuccess && (
          <div className="mb-6 p-4 bg-mint-50 border border-mint-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-mint-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-mint-800">Devis accepte avec succes !</p>
              <p className="text-sm text-mint-600">
                Vous recevrez prochainement un email de confirmation ainsi que la facture correspondante.
              </p>
            </div>
          </div>
        )}

        {feedbackSuccess && (
          <div className="mb-6 p-4 bg-gold-50 border border-gold-200 rounded-lg flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-gold-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-gold-800">Message envoye !</p>
              <p className="text-sm text-gold-600">
                Nous avons bien recu votre demande et reviendrons vers vous rapidement.
              </p>
            </div>
          </div>
        )}

        <Card padding="none" className="overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-beige-200">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h1 className="text-xl font-bold text-brown-900">LesSablesWeb</h1>
                <p className="text-sm text-brown-500">Agence Web & Marketing Digital</p>
                <p className="text-sm text-brown-500 mt-1">Les Sables-d'Olonne, France</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <FileText className="w-5 h-5 text-gold-600" />
                  <h2 className="text-lg font-bold text-brown-900">Devis {devis.numero}</h2>
                </div>
                <p className="text-sm text-brown-500 mt-1">
                  Emis le {formatDate(devis.date_emission)}
                </p>
                <div className="flex items-center gap-2 justify-end mt-1">
                  <Clock className="w-4 h-4 text-brown-400" />
                  <span className="text-sm text-brown-500">
                    Valide jusqu'au {formatDate(devis.date_validite)}
                  </span>
                </div>
                {isExpired && (
                  <Badge variant="red" className="mt-2">
                    Expire
                  </Badge>
                )}
                {devis.status === 'accepted' && (
                  <Badge variant="mint" className="mt-2">
                    Accepte
                  </Badge>
                )}
                {devis.status === 'rejected' && (
                  <Badge variant="red" className="mt-2">
                    Refuse
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Client info */}
          {devis.contact && (
            <div className="p-6 bg-beige-50 border-b border-beige-200">
              <p className="text-xs text-brown-500 uppercase tracking-wider mb-2">Client</p>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-mint-700 font-semibold text-sm">
                    {devis.contact.prenom?.charAt(0) || ''}{devis.contact.nom?.charAt(0) || ''}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-brown-900">
                    {devis.contact.prenom} {devis.contact.nom}
                  </p>
                  {devis.contact.entreprise && (
                    <p className="text-sm text-brown-600 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {devis.contact.entreprise}
                    </p>
                  )}
                  <p className="text-sm text-brown-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {devis.contact.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Title and description */}
            <div>
              <h3 className="text-lg font-semibold text-brown-900">{devis.titre}</h3>
              {devis.description && (
                <p className="mt-2 text-brown-600 whitespace-pre-line">{devis.description}</p>
              )}
            </div>

            {/* Lines table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-beige-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-brown-600">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-brown-600">
                      Qte
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-brown-600">
                      Prix unit. HT
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-brown-600">
                      Total HT
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {devis.lignes?.map((ligne, i) => (
                    <tr key={ligne.id || i} className="border-b border-beige-100">
                      <td className="px-4 py-3 text-brown-900">{ligne.description}</td>
                      <td className="px-4 py-3 text-right text-brown-600">{ligne.quantite}</td>
                      <td className="px-4 py-3 text-right text-brown-600">
                        {formatCurrency(ligne.prix_unitaire)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-brown-900">
                        {formatCurrency(ligne.total_ht)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-brown-500">Sous-total HT</span>
                  <span className="font-medium">{formatCurrency(devis.montant_ht)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-brown-500">TVA ({devis.taux_tva}%)</span>
                  <span>{formatCurrency(devis.montant_tva)}</span>
                </div>
                <div className="pt-2 border-t border-beige-200 flex justify-between text-lg font-bold">
                  <span className="text-brown-900">Total TTC</span>
                  <span className="text-mint-600">{formatCurrency(devis.montant_ttc)}</span>
                </div>
              </div>
            </div>

            {/* Conditions */}
            {devis.conditions && (
              <div className="bg-beige-50 p-4 rounded-lg">
                <h4 className="font-medium text-brown-900 mb-2">Conditions</h4>
                <p className="text-sm text-brown-600 whitespace-pre-line">{devis.conditions}</p>
              </div>
            )}
          </div>

          {/* Footer / Actions */}
          {isExpired ? (
            <div className="p-6 bg-red-50 border-t border-red-200">
              <div className="flex items-center gap-3 justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <span className="text-red-700 font-medium">
                  Ce devis a expire le {formatDate(devis.date_validite)}
                </span>
              </div>
              <p className="text-center text-sm text-red-600 mt-2">
                Veuillez nous contacter pour obtenir un nouveau devis actualise.
              </p>
            </div>
          ) : devis.status === 'accepted' ? (
            <div className="p-6 bg-mint-50 border-t border-mint-200">
              <div className="flex items-center gap-3 justify-center">
                <CheckCircle className="w-6 h-6 text-mint-600" />
                <span className="text-mint-700 font-medium">
                  Vous avez accepte ce devis le {formatDate(devis.date_reponse || '')}
                </span>
              </div>
              {devis.signature_client && devis.signature_client.startsWith('data:image') && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-mint-600 mb-2">Votre signature :</p>
                  <img
                    src={devis.signature_client}
                    alt="Signature"
                    className="max-w-[200px] mx-auto border border-mint-200 rounded bg-white p-2"
                  />
                </div>
              )}
            </div>
          ) : devis.status === 'rejected' ? (
            <div className="p-6 bg-red-50 border-t border-red-200">
              <div className="flex items-center gap-3 justify-center">
                <XCircle className="w-6 h-6 text-red-500" />
                <span className="text-red-700 font-medium">
                  Ce devis a ete decline
                </span>
              </div>
              {devis.message_client && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-red-200">
                  <p className="text-sm text-brown-500 mb-1">Votre message :</p>
                  <p className="text-brown-700">{devis.message_client}</p>
                </div>
              )}
            </div>
          ) : canInteract ? (
            <div className="p-6 bg-beige-50 border-t border-beige-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowFeedbackModal(true)}
                  leftIcon={<MessageSquare className="w-4 h-4" />}
                >
                  Demander des modifications
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => setShowSignatureModal(true)}
                  leftIcon={<Check className="w-4 h-4" />}
                >
                  Accepter ce devis
                </Button>
              </div>
            </div>
          ) : null}
        </Card>

        {/* Legal footer */}
        <div className="mt-8 text-center text-xs text-brown-400">
          <p>LesSablesWeb - SIRET: XXX XXX XXX XXXXX</p>
          <p className="mt-1">
            Ce document a valeur de proposition commerciale.
            Une fois signe, il engage les deux parties.
          </p>
        </div>
      </div>

      {/* Modals */}
      {devis && (
        <>
          <SignatureModal
            devis={devis}
            isOpen={showSignatureModal}
            onClose={() => setShowSignatureModal(false)}
            onSign={handleSign}
            isLoading={isSigning}
          />

          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
            onSubmit={handleReject}
            isLoading={isRejecting}
          />
        </>
      )}
    </div>
  )
}
